import { flatten } from '@onepercentio/one-ui/utils/flatten'
import { ChainConfig, ERC20ABI, Stable, chainConfig } from 'core/constants'
import { useTenant } from 'core/logic/tenant/tenant.hook'
import useWeb3Provider from 'hooks/useWeb3Provider'
import useLanguage from 'openspace/hooks/useLanguage'
import { createContext, useContext, useMemo } from 'react'
import { i18n } from 'translate/i18n'
import chainEn from 'translate/languages/chain.en'
import chainEs from 'translate/languages/chain.es'
import chainPt from 'translate/languages/chain.pt'
import { useCypressRequirementChecker } from 'utility/e2e'

export type Chains = 'POLYGON' | 'MOONBEAM' | 'ETHEREUM'

export type ChainContextShape = ChainConfig | undefined

export const ChainContext = createContext<ChainContextShape>(null as any)

const ChainProvider = ({ children }: { children: React.ReactNode }) => {
  const { tenant } = useTenant()
  const lang = useLanguage()

  const tenantChain = useMemo(() => {
    if (tenant) return tenant.chain ?? 'POLYGON'
    else return undefined
  }, [tenant])

  const registerTranslations = (
    lang: LanguageCodes,
    translations: typeof chainPt,
    chain: Chains
  ) => {
    const translationsObject =
      process.env.NODE_ENV === 'development'
        ? translations[chain] ?? translations.POLYGON
        : translations[chain]
    const flatTranslations = flatten(translationsObject) as any
    for (const key in flatTranslations) {
      if (!i18n.getResource(lang, 'translations', key))
        i18n.addResource(lang, 'translations', key, flatTranslations[key])
    }
  }

  const currentChain = useMemo(() => {
    if (tenantChain) {
      switch (lang) {
        case 'pt-BR':
          registerTranslations('pt-BR', chainPt, tenantChain)
          break
        case 'en-US':
          registerTranslations('en-US', chainEn, tenantChain)
          break
        case 'es-ES':
          registerTranslations('es-ES', chainEs, tenantChain)
          break
        default:
          break
      }
    }
    return tenantChain ? chainConfig[tenantChain] : undefined
  }, [tenant, lang])

  useCypressRequirementChecker('chain', tenantChain)

  return (
    <ChainContext.Provider value={currentChain}>
      {children}
    </ChainContext.Provider>
  )
}

export function useChain() {
  return useContext(ChainContext)
}

export function useChainForIntl(): Pick<
  ChainConfig,
  'name' | 'currency' | 'explorerUrl' | 'explorerName'
> {
  return useContext(ChainContext)!
}

export function useStable() {
  const stableTokensInformation = useContext(ChainContext)?.stableTokens

  return useMemo(() => {
    if (!stableTokensInformation)
      return {
        stableInfo: undefined,
        stableList: undefined,
      }
    const list = stableTokensInformation
    return {
      stableInfo: list.reduce(
        (r, stable) => ({
          ...r,
          [stable.symbol]: stable,
        }),
        {} as { [stable in Stable['symbol']]: Stable }
      ),
      stableList: list,
    }
  }, [stableTokensInformation])
}

type StableContract = {
  [c: CryptoCurrencySymbol]: Stable & {
    contract: Contract.IERC20
  }
}
export function useStableContracts(): StableContract {
  const { stableList } = useStable()
  const web3MetamaskProvider = useWeb3Provider()

  return useMemo(() => {
    if (!web3MetamaskProvider || !stableList) return {}
    return stableList.reduce(
      (r, token) => ({
        ...r,
        [token.symbol]: {
          ...token,
          contract: new web3MetamaskProvider.eth.Contract(
            ERC20ABI,
            token.address
          ) as any,
        },
      }),
      {} as StableContract
    )
  }, [web3MetamaskProvider])
}

export default ChainProvider
